/**
 * B2B 共通の身元（メール確認コードによるログイン）。
 *
 * developer-console（developer-db）・trainer 線（app-api / app-db）・merchant-api（biz-db）が **同じ手順・同じ数字・
 * 同じ文言** でログインするための単一の真実。設計 = `docs/architecture/b2b-onboarding.md` §8。
 *
 * 約束:
 * - ログインの鍵は**承認したメールアドレスそのもの**（パスワードも Apple ID も持たない）。
 *   承認台帳（`b2b_applications`）の email と一致するので、運営が見る名前が 1 つになる。
 * - コードは 6 桁・{@link B2B_LOGIN_CODE_TTL_SEC} 秒・{@link B2B_LOGIN_CODE_MAX_ATTEMPTS} 回で
 *   使い捨て。DB は SHA-256(email + code) だけを持つ（平文のコードを残さない）。
 * - **総当たりの上限はメールごとの窓で数える**（{@link B2B_LOGIN_WINDOW_SEC} 秒）。照合の失敗は
 *   再発行をまたいで累積し {@link B2B_LOGIN_MAX_FAILURES_PER_WINDOW} 回でログインを止め、
 *   発行は {@link B2B_LOGIN_MAX_ISSUES_PER_WINDOW} 回まで。1 本あたりの 5 回だけだと
 *   「外す → 再発行」を繰り返して実質無制限に試せた（2026-09-26 に塞いだ）。
 * - **コードの発行はメールの存在を漏らさない**（登録の無い宛先でも同じ応答・同じ所要時間）。
 *   誰が契約者かを外から列挙できる面にしない。発行の上限・ロックで断ったときも応答は変えない
 *   （{@link issueLoginCode} が null を返すだけ。呼び手は「送った」画面のまま、メールを送らない）。
 * - テーブル `b2b_login_codes` は **3 DB に同じ DDL** で置く（migrations-app `0094`・`0101` /
 *   migrations-developer `0008`・`0009` / migrations-biz `0009`）。列を足すときは 3 つともに足す。
 *
 * ⚠️ ここに置くのは **D1 に対する純粋な手順** まで。メール送信（`send_email` binding）と
 *    セッション（cookie / JWT）は各 worker の責務 — 「DB を持つ worker がセッションを検証する」
 *    （console は自前 cookie、trainer は app-api が発行する会員 JWT）。
 */
import { sha256Hex } from "./hash/index.js";
/** 確認コードの有効期間（秒）。メールの到着遅延を見込んで 10 分。 */
export const B2B_LOGIN_CODE_TTL_SEC = 600;
/** 同じコードに対する照合の上限。超えたらコードを捨てて再発行を求める（6 桁の総当たり対策）。 */
export const B2B_LOGIN_CODE_MAX_ATTEMPTS = 5;
/**
 * 失敗・発行を数える窓（秒）。メールごとに 1 本。窓が明けるか、ロックが明けると数え直す。
 * 1 時間 = 正規の利用者が「送る → 打ち間違える → 送り直す」を何度か繰り返しても収まる長さ。
 */
export const B2B_LOGIN_WINDOW_SEC = 3600;
/**
 * 窓の中で許す照合の失敗（**再発行をまたいで累積**）。達したら {@link B2B_LOGIN_LOCK_SEC} 秒止める。
 * 10 回 = 1 時間に当たる確率が 10 / 10^6。正規の利用者の打ち間違いは 1 本あたり高々数回なので、
 * 再発行 2 本分の失敗までは巻き込まない。
 */
export const B2B_LOGIN_MAX_FAILURES_PER_WINDOW = 10;
/**
 * 窓の中で許すコードの発行。メール爆撃（他人のアドレスへ送り続ける）の頭打ちで、
 * 失敗の累積と合わせて「再発行で照合回数を戻す」抜け道も閉じる。
 */
export const B2B_LOGIN_MAX_ISSUES_PER_WINDOW = 5;
/**
 * 失敗が上限に達したときに発行も照合も止める時間（秒）。窓と同じ長さにしておくと、
 * 攻撃側が窓の終わり際に失敗を寄せても 1 時間あたりの試行が {@link B2B_LOGIN_MAX_FAILURES_PER_WINDOW}
 * を大きく超えない。⚠️ 止めるのは「そのメールのログイン」なので、知られたアドレスを締め出す
 * 嫌がらせには使える — 長くしすぎない（正規の利用者は 1 時間待てば入れる）。
 */
export const B2B_LOGIN_LOCK_SEC = 3600;
/** ログイン後のセッション寿命（秒）。業務画面なので 14 日で再確認させる。 */
export const B2B_SESSION_TTL_SEC = 14 * 24 * 3600;
/** 招待 URL の有効日数（developer / trainer とも同じ）。 */
export const B2B_INVITE_TTL_DAYS = 7;
/** メールアドレスの正規化（大小・前後空白を無視して同一視する）。空や形式外は null。 */
export function normalizeB2BEmail(raw) {
    if (typeof raw !== "string")
        return null;
    const email = raw.trim().toLowerCase();
    if (email.length < 3 || email.length > 254)
        return null;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        return null;
    return email;
}
/** 6 桁の数字コード（先頭 0 を含む。`crypto.getRandomValues` で一様に取る）。 */
export function generateLoginCode() {
    const buf = new Uint32Array(1);
    // 剰余の偏りを避ける: 2^32 を 10^6 で割り切れる範囲に丸めてから取る
    const limit = Math.floor(0x1_0000_0000 / 1_000_000) * 1_000_000;
    let n;
    do {
        crypto.getRandomValues(buf);
        n = buf[0];
    } while (n >= limit);
    return String(n % 1_000_000).padStart(6, "0");
}
/** DB に残す値。email を混ぜるのは、別の宛先のコードを流用されないため。 */
export function hashLoginCode(email, code) {
    return sha256Hex(`${email}\n${code}`);
}
/** 入力の形（6 桁・空白/ハイフン許容）。照合前の正規化。 */
export function normalizeLoginCode(raw) {
    if (typeof raw !== "string")
        return null;
    const code = raw.replace(/[\s-]/g, "");
    return /^\d{6}$/.test(code) ? code : null;
}
// ── 窓の判定（SQL の断片）。数値は上の定数だけから作る（利用者の入力は混ぜない） ──
//
// ⚠️ 真偽が NULL にならない形で書く。`locked_until <= now` は NULL 列で NULL になり、
//    `NOT (…)` や JS 側の真偽判定で「偽」と「不明」が混ざる。COALESCE / IS NULL で 0/1 に潰す。
/** ロック中か（0/1）。 */
const LOCKED = "COALESCE(locked_until > datetime('now'), 0)";
/**
 * 窓を始め直すか（0/1）: 未記録（この列が無かった頃の行）・窓が明けた・ロックが明けた。
 * ロック明けでも始め直すのは、LOCK と WINDOW の長さの関係に判定を依存させないため。
 * 始め直すときは locked_until も NULL に戻す（過去のロック時刻が残ると、毎回始め直して数が貯まらない）。
 */
const WINDOW_RESET = `(window_started_at IS NULL
  OR window_started_at <= datetime('now', '-${B2B_LOGIN_WINDOW_SEC} seconds')
  OR COALESCE(locked_until <= datetime('now'), 0))`;
/**
 * コードを発行して保存する（1 メールにつき最新の 1 本だけ。再発行で前のコードは無効）。
 * 呼び出し側はこの戻り値をメールに載せる。
 *
 * **null = 発行しなかった**（ロック中、または窓の発行上限）。⚠️ 呼び手は利用者への応答を
 * 変えずにメールだけ省くこと — ここで 429 などを返すと、コードは登録済みの宛先にしか発行しないので
 * 「断られた = 登録済み」と外から判別できてしまう。
 *
 * 窓の判定と加算は **1 文の UPSERT** で行う（読んでから書く 2 往復にすると、同時に送られた分が
 * 上限を素通りする）。前のコードの照合回数（attempts）は 0 に戻すが、窓の失敗数は戻さない。
 */
export async function issueLoginCode(db, email) {
    const code = generateLoginCode();
    const codeHash = await hashLoginCode(email, code);
    const issued = await db
        .prepare(`INSERT INTO b2b_login_codes
         (email, code_hash, expires_at, attempts, created_at, window_started_at, window_issues, window_failures, locked_until)
       VALUES (?, ?, datetime('now', ?), 0, datetime('now'), datetime('now'), 1, 0, NULL)
       ON CONFLICT(email) DO UPDATE SET
         code_hash = excluded.code_hash,
         expires_at = excluded.expires_at,
         attempts = 0,
         created_at = excluded.created_at,
         window_started_at = CASE WHEN ${WINDOW_RESET} THEN excluded.window_started_at ELSE window_started_at END,
         window_issues = CASE WHEN ${WINDOW_RESET} THEN 1 ELSE window_issues + 1 END,
         window_failures = CASE WHEN ${WINDOW_RESET} THEN 0 ELSE window_failures END,
         locked_until = CASE WHEN ${WINDOW_RESET} THEN NULL ELSE locked_until END
       WHERE NOT ${LOCKED}
         AND (${WINDOW_RESET} OR window_issues < ${B2B_LOGIN_MAX_ISSUES_PER_WINDOW})
       RETURNING email`)
        .bind(email, codeHash, `+${B2B_LOGIN_CODE_TTL_SEC} seconds`)
        .first();
    return issued ? code : null;
}
/**
 * コードを照合し、成功なら消費する（1 回限り）。
 *
 * 手順は「**先に 1 回分を失敗として確保してから比べる**」:
 *   1. 条件付き UPDATE … RETURNING で attempts と窓の失敗数を 1 つ進める（上限・期限・ロックは WHERE で見る）。
 *      1 文なので、同時に何本送られても確保できるのは上限の本数まで。
 *   2. 確保できた 1 回でだけハッシュを比べる。一致したら `code_hash` 一致を条件に行を消す
 *      （同じコードの同時送信で 2 回ログインさせない。成功で窓の数も消える = 本人が入れた時点で数え直し）。
 *   3. 確保できなければ、理由を読み直して返す（期限切れ・上限・ロック）。
 *
 * 期限切れ・上限到達の行は**消さない**（消すと窓の失敗数まで消える）。次の発行が上書きする。
 */
export async function consumeLoginCode(db, email, code) {
    const expected = await hashLoginCode(email, code);
    const failuresAfter = `(CASE WHEN ${WINDOW_RESET} THEN 1 ELSE window_failures + 1 END)`;
    const reserved = await db
        .prepare(`UPDATE b2b_login_codes SET
         attempts = attempts + 1,
         window_started_at = CASE WHEN ${WINDOW_RESET} THEN datetime('now') ELSE window_started_at END,
         window_issues = CASE WHEN ${WINDOW_RESET} THEN 0 ELSE window_issues END,
         window_failures = ${failuresAfter},
         locked_until = CASE
           WHEN ${failuresAfter} >= ${B2B_LOGIN_MAX_FAILURES_PER_WINDOW} THEN datetime('now', '+${B2B_LOGIN_LOCK_SEC} seconds')
           WHEN ${WINDOW_RESET} THEN NULL
           ELSE locked_until END
       WHERE email = ?
         AND expires_at >= datetime('now')
         AND attempts < ${B2B_LOGIN_CODE_MAX_ATTEMPTS}
         AND NOT ${LOCKED}
         AND (${WINDOW_RESET} OR window_failures < ${B2B_LOGIN_MAX_FAILURES_PER_WINDOW})
       RETURNING code_hash, attempts, ${LOCKED} AS locked`)
        .bind(email)
        .first();
    if (reserved) {
        if (expected === reserved.code_hash) {
            const consumed = await db
                .prepare("DELETE FROM b2b_login_codes WHERE email = ? AND code_hash = ? RETURNING email")
                .bind(email, expected)
                .first();
            // 消せなかった = 同じコードの同時送信に先を越された / その間に再発行された
            return consumed ? "ok" : "invalid";
        }
        if (reserved.locked)
            return "throttled";
        return reserved.attempts >= B2B_LOGIN_CODE_MAX_ATTEMPTS ? "locked" : "invalid";
    }
    const row = await db
        .prepare(`SELECT attempts, (expires_at < datetime('now')) AS expired,
              (${LOCKED} OR (NOT ${WINDOW_RESET} AND window_failures >= ${B2B_LOGIN_MAX_FAILURES_PER_WINDOW})) AS throttled
       FROM b2b_login_codes WHERE email = ?`)
        .bind(email)
        .first();
    if (!row)
        return "invalid";
    if (row.throttled)
        return "throttled";
    if (row.expired)
        return "expired";
    if (row.attempts >= B2B_LOGIN_CODE_MAX_ATTEMPTS)
        return "locked";
    return "invalid";
}
/** 照合結果 → 利用者に見せる文言（両線で同じ言い方にする）。 */
export const LOGIN_CODE_ERROR_MESSAGES = {
    invalid: "確認コードが違います。メールに届いた 6 桁の数字を入力してください。",
    expired: "確認コードの有効期限（10 分）が切れました。もう一度コードを送信してください。",
    locked: "確認コードの入力回数が上限に達しました。もう一度コードを送信してください。",
    throttled: `確認コードの入力に続けて失敗したため、このメールアドレスのログインを一時的に止めています。${Math.round(B2B_LOGIN_LOCK_SEC / 60)} 分ほど待ってから、もう一度コードを送信してください。`,
};
/**
 * コード入力画面の「届かない場合」に添える 1 文（両線で同じ）。発行の上限・ロックでは画面を変えずに
 * メールだけ送らない（{@link issueLoginCode}）ので、届かない理由の 1 つとしてここで伝える。
 */
export const LOGIN_CODE_RESEND_LIMIT_NOTE = `短時間に何度も送信した場合は、しばらく（最長 ${Math.round(Math.max(B2B_LOGIN_WINDOW_SEC, B2B_LOGIN_LOCK_SEC) / 60)} 分）送信を止めています。`;
/**
 * 確認コードのメール本文。件名に製品名を入れ、本文は両線で同一（受信者が複数線を使っても
 * 同じ形で届く）。⚠️ 本文にリンクを入れない — コードを打つ画面は利用者が既に開いている。
 */
export function loginCodeMail(input) {
    const minutes = Math.round(B2B_LOGIN_CODE_TTL_SEC / 60);
    return {
        subject: `【${input.productName}】ログイン確認コード: ${input.code}`,
        text: [
            `${input.productName} のログイン確認コードです。`,
            "",
            `    ${input.code}`,
            "",
            `このコードは ${minutes} 分間有効で、1 回だけ使えます。`,
            "ログインを試みていない場合は、このメールを破棄してください（第三者がこのアドレスを入力しただけで、アカウントには影響しません）。",
            "",
            "kalbi 合同会社 / kalori",
        ].join("\n"),
    };
}
//# sourceMappingURL=business-login.js.map